export type MetricChartDataRow = Record<string, unknown> & {
    date?: string;
    periodLabel?: string;
};

export interface MetricChartCompany {
    companyName: string;
    exchange?: string;
    image?: string;
    marketCap?: number;
    sector?: string;
    symbol: string;
    website?: string;
}

export interface MetricChartComparisonDataset {
    company: MetricChartCompany;
    data: MetricChartDataRow[];
}

export interface MetricChartSeries {
    company: MetricChartCompany;
    companyIndex: number;
    dataKey: string;
    lineItem: string;
    lineItemIndex: number;
}

const nonEmptyString = (value: unknown) => (typeof value === 'string' && value.trim() ? value.trim() : undefined);

export const normalizeMetricChartCompany = (
    candidate: unknown,
    fallbackSymbol?: string,
): MetricChartCompany | undefined => {
    if (!candidate || typeof candidate !== 'object') {
        return fallbackSymbol ? { companyName: fallbackSymbol, symbol: fallbackSymbol } : undefined;
    }

    const record = candidate as Record<string, unknown>;
    const enriched =
        record.enriched && typeof record.enriched === 'object' ? (record.enriched as Record<string, unknown>) : record;
    const symbol =
        nonEmptyString(enriched.symbol) ??
        nonEmptyString(record.symbol) ??
        nonEmptyString(enriched.tickerSymbol) ??
        nonEmptyString(record.tickerSymbol) ??
        fallbackSymbol;

    if (!symbol) {
        return undefined;
    }

    const marketCap = Number(enriched.marketCap ?? record.marketCap);

    return {
        companyName:
            nonEmptyString(record.editedName) ??
            nonEmptyString(record.companyName) ??
            nonEmptyString(enriched.companyName) ??
            nonEmptyString(record.name) ??
            nonEmptyString(enriched.name) ??
            symbol,
        exchange: nonEmptyString(enriched.exchange) ?? nonEmptyString(record.exchange),
        image: nonEmptyString(enriched.image) ?? nonEmptyString(record.image),
        marketCap: Number.isFinite(marketCap) ? marketCap : undefined,
        sector: nonEmptyString(enriched.sector) ?? nonEmptyString(record.sector),
        symbol,
        website: nonEmptyString(enriched.website) ?? nonEmptyString(record.website),
    };
};

export const financialStatementEndpointForSymbol = (endpoint: string, currentSymbol: string, nextSymbol: string) => {
    const segments = endpoint.split('/');
    const symbolIndex = segments.findIndex((segment) => segment === currentSymbol);

    if (symbolIndex === -1) {
        return endpoint;
    }

    segments[symbolIndex] = nextSymbol;

    return segments.join('/');
};

export const metricComparisonDataKey = (symbol: string, lineItem: string) => `${symbol}::${lineItem}`;

export const metricChartSeriesDataKey = (companyIndex: number, symbol: string, lineItem: string) =>
    companyIndex === 0 ? lineItem : metricComparisonDataKey(symbol, lineItem);

export const metricChartSeriesVisibilityKey = (symbol: string, lineItem: string) =>
    metricComparisonDataKey(symbol, lineItem);

export const metricChartCompanySetKey = (companies: MetricChartCompany[]) =>
    [...new Set(companies.map((company) => company.symbol).filter(Boolean))].sort().join('|');

export const reorderMetricChartComparisonCompanies = (
    companies: MetricChartCompany[],
    sourceIndex: number,
    destinationIndex: number,
) => {
    const comparisonCompanies = companies.slice(1);
    const sourceComparisonIndex = sourceIndex - 1;

    if (
        sourceComparisonIndex < 0 ||
        sourceComparisonIndex >= comparisonCompanies.length ||
        sourceIndex === destinationIndex
    ) {
        return comparisonCompanies;
    }

    const [movedCompany] = comparisonCompanies.splice(sourceComparisonIndex, 1);
    const destinationComparisonIndex = Math.min(comparisonCompanies.length, Math.max(0, destinationIndex - 1));

    comparisonCompanies.splice(destinationComparisonIndex, 0, movedCompany);

    return comparisonCompanies;
};

export const createMetricChartSeries = (
    tags: string[],
    primaryCompany: MetricChartCompany | undefined,
    comparisonCompanies: MetricChartCompany[] = [],
): MetricChartSeries[] => {
    const primary = primaryCompany ?? { companyName: 'Primary company', symbol: 'primary' };

    return [primary, ...comparisonCompanies].flatMap((company, companyIndex) =>
        tags.map((lineItem, lineItemIndex) => ({
            company,
            companyIndex,
            dataKey: metricChartSeriesDataKey(companyIndex, company.symbol, lineItem),
            lineItem,
            lineItemIndex,
        })),
    );
};

const rowKey = (row: MetricChartDataRow) => String(row.periodLabel ?? row.date ?? '');

export const mergeMetricChartData = (
    primaryData: MetricChartDataRow[],
    comparisonData: MetricChartComparisonDataset[],
    tags: string[],
) => {
    const rows = new Map<string, MetricChartDataRow>();

    primaryData.forEach((row) => rows.set(rowKey(row), { ...row }));
    comparisonData.forEach(({ company, data }) => {
        data.forEach((row) => {
            const key = rowKey(row);

            if (!key) {
                return;
            }

            const mergedRow = rows.get(key) ?? { date: row.date, periodLabel: row.periodLabel };

            tags.forEach((lineItem) => {
                mergedRow[metricComparisonDataKey(company.symbol, lineItem)] = row[lineItem];
            });
            rows.set(key, mergedRow);
        });
    });

    return [...rows.values()].sort(
        (a, b) => new Date(String(a.date ?? '')).getTime() - new Date(String(b.date ?? '')).getTime(),
    );
};
